import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Table,
  Spinner,
  Alert,
  Container,
  Pagination,
  Row,
  Col,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

function Click2payTable() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [limit] = useState(20); // Items per page
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  const fetchOrders = useCallback(
    async (page = 1) => {
      if (userInfo && (userInfo.isAdmin || userInfo.isOffice)) {
        try {
          setLoading(true);
          const response = await axios.get(
            `/api/clic2pay/order-statuses?page=${page}&limit=${limit}`
          );

          // Remove duplicates based on orderId
          const uniqueOrders = response.data.orders.reduce((acc, order) => {
            if (!acc.find((o) => o.orderId === order.orderId)) {
              acc.push(order);
            }
            return acc;
          }, []);

          setOrders(uniqueOrders);
          setCurrentPage(response.data.pagination.currentPage);
          setTotalPages(response.data.pagination.totalPages);
          setTotalOrders(response.data.pagination.totalOrders);
          setLoading(false);
        } catch (error) {
          setLoading(false);
          if (error.response && error.response.status === 401) {
            navigate('/login');
          } else {
            setError('Error fetching order statuses.');
            console.error('Error:', error.message);
          }
        }
      }
    },
    [navigate, userInfo, limit]
  );

  useEffect(() => {
    fetchOrders(currentPage);
    const interval = setInterval(() => fetchOrders(currentPage), 60000);
    return () => clearInterval(interval);
  }, [fetchOrders, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchOrders(page);
  };

  const renderPaginationItems = () => {
    let items = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page and previous arrow
    items.push(
      <Pagination.First
        key='first'
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
      />
    );
    items.push(
      <Pagination.Prev
        key='prev'
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    // Page numbers
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Next arrow and last page
    items.push(
      <Pagination.Next
        key='next'
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );
    items.push(
      <Pagination.Last
        key='last'
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  if (loading)
    return (
      <div className='text-center my-5'>
        <Spinner animation='border' role='status' className='my-3'>
          <span className='visually-hidden'>Loading...</span>
        </Spinner>
        <p>Loading orders...</p>
      </div>
    );

  if (error) return <Alert variant='danger'>{error}</Alert>;

  return (
    <Container>
      <h2 className='my-4'>Table Status de Paiement Click2Pay</h2>

      {/* Pagination Info - Top (only showing count, no pagination controls) */}
      <Row className='mb-3'>
        <Col>
          <div className='d-flex justify-content-between align-items-center'>
            <p className='mb-0'>
              Showing {(currentPage - 1) * limit + 1} to{' '}
              {Math.min(currentPage * limit, totalOrders)} of {totalOrders}{' '}
              orders
            </p>
          </div>
        </Col>
      </Row>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Date</th>
            <th>Order ID</th>
            <th>Status</th>
            <th>Montant</th>
            <th>Client</th>
            <th>Error Code</th>
            <th>Error Message</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.orderId}>
              <td>
                {new Date(order.createdAt).toLocaleDateString('fr-FR')}{' '}
                {new Date(order.createdAt).toLocaleTimeString('fr-FR')}
              </td>
              <td>{order.orderId}</td>
              <td>{order.OrderStatus}</td>
              <td>{(order.Amount / 1000).toFixed(2)} DT</td>
              <td>{order.cardholderName || 'N/A'}</td>
              <td>{order.ErrorCode || 'N/A'}</td>
              <td>{order.ErrorMessage || 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Bottom Pagination with controls */}
      {totalPages > 1 && (
        <Row className='mt-3'>
          <Col>
            <div className='d-flex justify-content-between align-items-center'>
              <p className='mb-0'>
                Page {currentPage} of {totalPages}
              </p>
              <Pagination className='mb-0'>
                {renderPaginationItems()}
              </Pagination>
            </div>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default Click2payTable;
